import React, { useState, useEffect } from "react";
import {
  FaEye,
  FaSpinner,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import Sidebar from "../SidebarInAdminPanel/Sidebar";
import { 
  fetchAllUserTransactions 
} from "../../Controllers/paymentController"; // Adjust the import path as necessary

const PaymentDetailsPage = () => {
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    successful: 0,
    pending: 0,
    failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPaymentData();
  }, []);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all transactions
      const transactionsResult = await fetchAllUserTransactions();
      if (!transactionsResult.success) {
        throw new Error(transactionsResult.error);
      }

      // Format transactions for display
      const formattedPayments = transactionsResult.transactions.map(formatTransactionForDisplay);
      setPayments(formattedPayments);

      // Calculate statistics
      const totalTransactions = formattedPayments.length;
      const creditTransactions = formattedPayments.filter(t => t.originalData.type === 'credit').length;
      const debitTransactions = formattedPayments.filter(t => t.originalData.type === 'debit').length;

      setStats({
        total: totalTransactions,
        successful: creditTransactions,
        pending: 0,
        failed: debitTransactions,
      });

    } catch (err) {
      console.error("Error loading payment data:", err);
      setError(err.message || "Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const formatTransactionForDisplay = (transaction) => {
    const formatDate = (timestamp) => {
      if (!timestamp) return 'N/A';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-GB');
    };

    const formatTime = (timestamp) => {
      if (!timestamp) return 'N/A';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    };

    const getStatus = (transaction) => {
      if (transaction.type === 'credit') return 'Credit';
      if (transaction.type === 'debit') return 'Debit';
      return 'Unknown';
    };

    return {
      id: `#TXN${transaction.id.slice(-6).toUpperCase()}`,
      user: transaction.userEmail || 'Unknown User',
      amount: `₨ ${transaction.amount.toLocaleString()}`,
      status: getStatus(transaction),
      date: formatDate(transaction.createdAt),
      time: formatTime(transaction.createdAt),
      originalData: transaction,
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Credit":
        return "text-green-600";
      case "Debit":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Credit":
        return <FaArrowUp />;
      case "Debit":
        return <FaArrowDown />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
        <Sidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="text-white text-center">
            <FaSpinner className="animate-spin text-4xl mb-4 mx-auto" />
            <p className="text-xl">Loading payment data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
        <Sidebar />
        <div className="flex-1 p-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p className="font-bold">Error loading payment data:</p>
            <p>{error}</p>
            <button 
              onClick={loadPaymentData}
              className="mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-white">Payment Details</h1>
          <button
            onClick={loadPaymentData}
            className="bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 rounded shadow"
          >
            Refresh
          </button>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <p className="text-lg font-semibold text-gray-700">Total Transactions</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-green-100 p-4 rounded-lg shadow-md">
            <p className="text-lg font-semibold text-green-800">Credits</p>
            <p className="text-3xl font-bold text-green-600">{stats.successful}</p>
          </div>
          <div className="bg-red-100 p-4 rounded-lg shadow-md">
            <p className="text-lg font-semibold text-red-800">Debits</p>
            <p className="text-3xl font-bold text-red-600">{stats.failed}</p>
          </div>
          <div className="bg-blue-100 p-4 rounded-lg shadow-md">
            <p className="text-lg font-semibold text-blue-800">Active Users</p>
            <p className="text-3xl font-bold text-blue-600">
              {new Set(payments.map(p => p.originalData.userId)).size}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-xl shadow-md">
          <table className="min-w-full divide-y divide-gray-200 text-lg">
            <thead className="bg-gray-100 text-gray-700 uppercase text-lg font-semibold">
              <tr>
                <th className="px-6 py-3 text-left">Transaction ID</th>
                <th className="px-6 py-3 text-left">User Email</th>
                <th className="px-6 py-3 text-left">Amount</th>
                <th className="px-6 py-3 text-left">Type</th>
                <th className="px-6 py-3 text-left">Date</th>
                <th className="px-6 py-3 text-left">Time</th>
                <th className="px-6 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No payment data available
                  </td>
                </tr>
              ) : (
                payments.map((payment, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-mono text-sm">{payment.id}</td>
                    <td className="px-6 py-4">{payment.user}</td>
                    <td className="px-6 py-4 font-semibold">{payment.amount}</td>
                    <td
                      className={`px-6 py-4 font-semibold flex items-center space-x-2 ${getStatusColor(
                        payment.status
                      )}`}
                    >
                      <span>{getStatusIcon(payment.status)}</span>
                      <span>{payment.status}</span>
                    </td>
                    <td className="px-6 py-4">{payment.date}</td>
                    <td className="px-6 py-4">{payment.time}</td>
                    <td className="px-6 py-4">
                      <button
                        className="text-blue-500 hover:underline flex items-center"
                        onClick={() => {
                          setSelectedPayment(payment);
                          setIsModalOpen(true);
                        }}
                      >
                        <FaEye className="inline-block mr-1" /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Enhanced Modal */}
        {isModalOpen && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl relative transform transition-all">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    selectedPayment.status === 'Credit' ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  Transaction Details
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold transition-colors"
                >
                  ×
                </button>
              </div>

              {/* Transaction Info Cards */}
              <div className="space-y-4">
                {/* Transaction ID Card */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Transaction ID</p>
                  <p className="font-mono text-lg font-semibold text-gray-800">
                    {selectedPayment.id}
                  </p>
                </div>

                {/* Amount Card */}
                <div className={`rounded-lg p-4 ${
                  selectedPayment.status === 'Credit' ? 'bg-green-50' : 'bg-red-50'
                }`}>
                  <p className="text-sm text-gray-600 mb-1">Amount</p>
                  <div className="flex items-center">
                    <span className={`text-2xl font-bold ${
                      selectedPayment.status === 'Credit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {selectedPayment.status === 'Credit' ? '+' : '-'}{selectedPayment.amount}
                    </span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                      selectedPayment.status === 'Credit' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedPayment.status}
                    </span>
                  </div>
                </div>

                {/* Balance Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600 mb-1">Balance Before</p>
                    <p className="text-xl font-semibold text-blue-800">
                      ₨ {selectedPayment.originalData.balanceBefore?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-purple-600 mb-1">Balance After</p>
                    <p className="text-xl font-semibold text-purple-800">
                      ₨ {selectedPayment.originalData.balanceAfter?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>

                {/* User Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">User Email</p>
                  <p className="text-lg font-medium text-gray-800">
                    {selectedPayment.originalData.userEmail}
                  </p>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Date</p>
                    <p className="text-lg font-medium text-gray-800">
                      {selectedPayment.date}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Time</p>
                    <p className="text-lg font-medium text-gray-800">
                      {selectedPayment.time}
                    </p>
                  </div>
                </div>

                {/* Description */}
                {selectedPayment.originalData.description && (
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-sm text-yellow-600 mb-1">Description</p>
                    <p className="text-lg font-medium text-yellow-800">
                      {selectedPayment.originalData.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentDetailsPage;